import { computed, effect, inject } from '@angular/core';

import {
  patchState,
  signalStore,
  type,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { combineLatest, pipe, switchMap, tap, timer } from 'rxjs';
import { AppError } from '@hglobal/common';
import { tapResponse } from '@ngrx/operators';
import {
  entityConfig,
  setAllEntities,
  updateEntity,
  withEntities,
} from '@ngrx/signals/entities';

import { AiChatFormService } from '../ai-chat-form.service';
import {
  AiChatFilters,
  Chat,
  ChatMessage,
  InternalMessageStatus,
  GetMessagesReq,
  LeaveFeedbackReq,
  ToggleReactionReq,
} from '../../../models';
import {
  setError,
  setPending,
  setSuccess,
  withRequestStatus,
} from '../../../helpers/request-status.feature';
import { getNewSessionGreeting } from '../helpers/greeting-messages';
import { DictionariesService } from '../../../services/dictionaries.service';

type AiChatState = {
  totalMessages: number;
  filters: AiChatFilters;
  isLoading: boolean;
  feedbackMessageId: string;
  userChats: Chat[];
  selectedChat: Chat;
  chatStarted: string;
};

export const initialFilters: AiChatFilters = {
  limit: 20,
  offset: 0,
};

export const initialState: AiChatState = {
  totalMessages: 0,
  filters: initialFilters,
  isLoading: false,
  feedbackMessageId: '',
  userChats: [],
  selectedChat: null,
  chatStarted: null,
};

const messageConfig = entityConfig({
  entity: type<ChatMessage>(),
  collection: 'message',
});

export const AiCharStore = signalStore(
  withState(initialState),
  withEntities(messageConfig),
  withRequestStatus(),
  withComputed(({ messageEntities, totalMessages, isLoading }) => ({
    messages: messageEntities,
    disableInfiniteScroll: computed(
      () => isLoading() || messageEntities().length - 1 >= totalMessages(),
    ),
    disableSendQuestion: computed(() => {
      const firstMessage = messageEntities()[0];

      return (
        firstMessage?.internalStatus === InternalMessageStatus.pending ||
        firstMessage?.internalStatus === InternalMessageStatus.timeout
      );
    }),
  })),
  withMethods(
    (
      store,
      aiChatFormService = inject(AiChatFormService),
      dictionariesService = inject(DictionariesService),
    ) => ({
      getUserChats: rxMethod<void>(
        pipe(
          tap(() => patchState(store, setPending())),
          switchMap(() =>
            aiChatFormService.getUserChats().pipe(
              tapResponse({
                next: ({ chats }) =>
                  patchState(store, setSuccess(), {
                    userChats: chats,
                    selectedChat: chats[0],
                  }),
                error: (error: AppError) => patchState(store, setError(error)),
              }),
            ),
          ),
        ),
      ),
      getMessages: rxMethod<GetMessagesReq>(
        pipe(
          tap(() => patchState(store, setPending())),
          switchMap((query) =>
            aiChatFormService.getMessages(query).pipe(
              tapResponse({
                next: (response) => {
                  if (query.offset > 0) {
                    patchState(
                      store,
                      setSuccess(),
                      { totalMessages: response.total },
                      setAllEntities(
                        [...store.messages(), ...response.messages],
                        messageConfig,
                      ),
                    );
                  } else {
                    patchState(
                      store,
                      setSuccess(),
                      {
                        totalMessages: response.total,
                        chatStarted: new Date().toISOString(),
                      },
                      setAllEntities(
                        [
                          getNewSessionGreeting(
                            query.chatId,
                            dictionariesService.translate(
                              response.messages.length > 0
                                ? 'chatBot.greeting.return'
                                : 'chatBot.greeting.first',
                              'en',
                            ),
                            response.messages?.[0]?.createdAt,
                          ),
                          ...response.messages,
                        ],
                        messageConfig,
                      ),
                    );
                  }
                },
                error: (error: AppError) => patchState(store, setError(error)),
              }),
            ),
          ),
        ),
      ),
      sendQuestion: rxMethod<ChatMessage>(
        pipe(
          tap((question) => {
            if (store.messages()[0].id === question.id) {
              patchState(
                store,
                updateEntity(
                  {
                    id: question.id,
                    changes: { internalStatus: InternalMessageStatus.pending },
                  },
                  messageConfig,
                ),
              );
            } else {
              patchState(
                store,
                setAllEntities([question, ...store.messages()], messageConfig),
              );
            }
          }),
          switchMap((question) => {
            return combineLatest([
              aiChatFormService
                .sendQuestion({
                  chatId: question.chatId,
                  messageId: question.id,
                  request: question.request,
                  chatStarted: question.chatStarted,
                })
                .pipe(
                  tapResponse({
                    next: (message) => {
                      patchState(
                        store,
                        updateEntity(
                          {
                            id: message.id,
                            changes: {
                              ...message,
                              internalStatus: InternalMessageStatus.resolved,
                            },
                          },
                          messageConfig,
                        ),
                      );
                    },
                    error: (error: AppError) => {
                      patchState(
                        store,
                        setError(error),
                        updateEntity(
                          {
                            id: question.id,
                            changes: {
                              internalStatus: InternalMessageStatus.failed,
                            },
                          },
                          messageConfig,
                        ),
                      );
                    },
                  }),
                ),
              timer(10000).pipe(
                tap(() => {
                  patchState(
                    store,
                    updateEntity(
                      {
                        id: question.id,
                        changes: (message) => ({
                          internalStatus:
                            message.internalStatus === InternalMessageStatus.pending
                              ? InternalMessageStatus.timeout
                              : message.internalStatus,
                        }),
                      },
                      messageConfig,
                    ),
                  );
                }),
              ),
            ]);
          }),
        ),
      ),
      leaveFeedback: rxMethod<LeaveFeedbackReq>(
        pipe(
          tap(({ id }) => {
            patchState(
              store,
              updateEntity(
                {
                  id,
                  changes: { isFeedbackProvided: true },
                },
                messageConfig,
              ),
            );
          }),
          switchMap((feedback) => aiChatFormService.leaveFeedback(feedback)),
        ),
      ),
      toggleReaction: rxMethod<ToggleReactionReq>(
        pipe(
          tap(({ id, reaction }) => {
            patchState(
              store,
              updateEntity(
                {
                  id,
                  changes: { reactionType: reaction },
                },
                messageConfig,
              ),
            );
          }),
          switchMap((toggleReactionReq) =>
            aiChatFormService.toggleReaction(toggleReactionReq),
          ),
        ),
      ),
      updateFilters(filters: Partial<AiChatFilters>): void {
        patchState(store, { filters: { ...store.filters(), ...filters } });
      },
      openFeedbackForm(id: string): void {
        patchState(store, { feedbackMessageId: id });
      },
      closeFeedbackForm(): void {
        patchState(store, { feedbackMessageId: '' });
      },
    }),
  ),
  withHooks({
    onInit(store) {
      store.getUserChats();

      effect(
        () => {
          if (store.selectedChat()) {
            store.getMessages({
              ...store.filters(),
              chatId: store.selectedChat().id,
            });
          }
        },
        { allowSignalWrites: true },
      );
    },
  }),
);
